import React, { useState, useEffect, useRef } from 'react';
import framework from 'utils/framework';
import helpersUtils from 'utils/Helpers';
const { wrapFunctionalComponent } = framework;
const { deferTaskExecution } = helpersUtils;
import {
    Image, Text, Flex, Box
} from 'components/ui';
import Flag from 'components/Flag/Flag';
import Chiclet from 'components/Chiclet';
import {
    space, radii, mediaQueries, forms, gradients, colors
} from 'style/config';
import MessageInputBar from 'ai/components/SuperChat/MessageInputBar/MessageInputBar';
import { AI_CHAT_IMAGE, AI_CHAT_BLACK_IMAGE } from 'ai/utils/imagePaths';
import { trackSuperChatPromptClick, getSuperChatPageContext } from 'ai/utils/trackSuperChatPromptClick';
import { useViewableImpression } from 'ai/analytics';
import { mergeLyraAdvice } from 'ai/utils/lyraAdviceCache';

function ProductPage(props) {
    const {
        localization,
        fetchEntryPointPromptsPdp,
        product,
        handlePromptClick,
        chatName,
        clientId,
        anonymousId,
        isRequestInFlight,
        updatePrompts,
        isGenAINewUIEnabled
    } = props;
    const [prompts, setPrompts] = useState([]);
    const componentRef = useRef(null);

    useEffect(() => {
        const fetchPrompts = async () => {
            const res = await fetchEntryPointPromptsPdp(product, clientId, anonymousId);
            setPrompts(res?.entrypoint_prompts || []);
            updatePrompts(res?.entrypoint_prompts || []);
            mergeLyraAdvice(res?.lyra_advice);
        };

        fetchPrompts();
    }, []);

    // Track viewable impression for Product Page
    useViewableImpression({
        componentRef,
        prompts,
        sotFields: {
            sotV15: product?.productDetails?.productId || product?.productId || ''
        },
        dependencies: [product]
    });

    const openAIChat = (prompt = null, source = null) => {
        if (isRequestInFlight) {
            return;
        }

        deferTaskExecution(() => {
            if (prompt) {
                const pageContext = getSuperChatPageContext({ product });
                trackSuperChatPromptClick({
                    promptText: prompt,
                    pageContext,
                    source
                });
            }

            handlePromptClick(prompts, product, prompt);
        });
    };

    return (
        <Box
            ref={componentRef}
            backgroundColor='lightBlue'
            padding={space[4]}
            marginBottom={space[5]}
            css={[styles.box, isGenAINewUIEnabled && styles.boxControl]}
            borderRadius={radii[1]}
        >
            <Flex
                alignItems='center'
                gap={`${space[2]}px`}
                css={styles.productAdvisorTitleRow}
            >
                <Image
                    disableLazyLoad={true}
                    alt='AI Chat'
                    src={isGenAINewUIEnabled ? AI_CHAT_BLACK_IMAGE : AI_CHAT_IMAGE}
                    width={space[5]}
                    height={space[5]}
                />
                <Text
                    is='h2'
                    fontSize={['md', 'lg']}
                    lineHeight='tight'
                    fontWeight='bold'
                >
                    {chatName}
                </Text>
                <Flag
                    backgroundColor={'black'}
                    children={localization.new}
                />
            </Flex>
            <Box
                marginTop={space[4]}
                width='624px'
                css={[styles.messageInputBar, isRequestInFlight && styles.disabled]}
                onClick={() => openAIChat()}
            >
                <MessageInputBar
                    placeholder={localization.aiChatInputPlaceholderPdp}
                    disabled={true}
                />
            </Box>
            <ul css={styles.suggestionList}>
                {Array.isArray(prompts) &&
                    prompts.length > 0 &&
                    prompts?.map((suggestion, index) => {
                        return (
                            <li
                                key={`suggestion_${suggestion?.content || index}`}
                                css={[styles.suggestionListItem, isRequestInFlight && styles.disabled]}
                                onClick={() => openAIChat(suggestion?.content, suggestion?.source)}
                            >
                                <Chiclet
                                    variant='shadow'
                                    children={suggestion?.content}
                                    css={isRequestInFlight ? styles.disabledChiclet : null}
                                />
                            </li>
                        );
                    })}
            </ul>
        </Box>
    );
}

const suggestionListStyle = {
    whiteSpace: 'nowrap',
    scrollbarWidth: 'none',
    marginRight: -space[4]
};

const styles = {
    box: {
        backgroundColor: colors.lightBlue,
        [mediaQueries.xsMax]: {
            borderRadius: radii[0],
            marginLeft: -space[4],
            marginRight: -space[4]
        }
    },
    boxControl: {
        backgroundColor: 'transparent',
        backgroundImage: gradients.aiBeautyChat
    },
    productAdvisorTitleRow: { flex: '0 0 auto' },
    suggestionList: {
        display: 'flex',
        overflowX: 'scroll',
        gap: space[3],
        cursor: 'pointer',
        marginTop: space[3],
        paddingLeft: space[4],
        marginLeft: -space[4],
        overflowY: 'visible',
        [mediaQueries.xsMax]: {
            ...suggestionListStyle,
            overflowX: 'auto',
            overflowY: 'visible',
            display: 'line-item',
            '&::-webkit-scrollbar': { display: 'none' },
            paddingTop: space[4] + space[1],
            paddingBottom: space[2],
            marginTop: -space[2],
            marginBottom: -space[2]
        },
        [mediaQueries.sm]: {
            ...suggestionListStyle,
            display: '-webkit-box'
        }
    },
    suggestionListItem: {
        display: 'inline-block',
        marginRight: space[3],
        [mediaQueries.xsMax]: {
            display: 'inline-block',
            marginRight: space[1]
        }
    },
    messageInputBar: {
        [mediaQueries.xsMax]: {
            width: '100%',
            marginTop: space[3]
        }
    },
    disabled: {
        opacity: forms.DISABLED_OPACITY,
        pointerEvents: 'none',
        cursor: 'not-allowed'
    },
    disabledChiclet: {
        opacity: forms.DISABLED_OPACITY
    }
};

export default wrapFunctionalComponent(ProductPage, 'ProductPage');
